import {
  CognitoUserPool,
  CognitoUser,
  AuthenticationDetails,
  CognitoUserSession,
} from 'amazon-cognito-identity-js';

const userPool = new CognitoUserPool({
  UserPoolId: process.env.REACT_APP_COGNITO_USER_POOL_ID!,
  ClientId: process.env.REACT_APP_COGNITO_APP_CLIENT_ID!,
});

// Holds the CognitoUser when Cognito requires a new password on first login.
let pendingNewPasswordUser: CognitoUser | null = null;

/**
 * Sign in with email + password.
 * Resolves with the ID token JWT on success.
 * Throws Error('NEW_PASSWORD_REQUIRED') when Cognito forces a password change.
 */
export const cognitoSignIn = (email: string, password: string): Promise<string> =>
  new Promise((resolve, reject) => {
    const user = new CognitoUser({ Username: email, Pool: userPool });
    user.setAuthenticationFlowType('USER_PASSWORD_AUTH');
    const authDetails = new AuthenticationDetails({ Username: email, Password: password });

    user.authenticateUser(authDetails, {
      onSuccess: (session: CognitoUserSession) => resolve(session.getIdToken().getJwtToken()),
      onFailure: (err) => reject(err),
      newPasswordRequired: () => {
        pendingNewPasswordUser = user;
        reject(new Error('NEW_PASSWORD_REQUIRED'));
      },
    });
  });

/**
 * Complete the forced password-change challenge (first login).
 * Must be called after cognitoSignIn throws NEW_PASSWORD_REQUIRED.
 */
export const cognitoCompleteNewPassword = (newPassword: string): Promise<string> =>
  new Promise((resolve, reject) => {
    if (!pendingNewPasswordUser) {
      reject(new Error('No pending password challenge'));
      return;
    }
    pendingNewPasswordUser.completeNewPasswordChallenge(
      newPassword,
      {},
      {
        onSuccess: (session: CognitoUserSession) => {
          pendingNewPasswordUser = null;
          resolve(session.getIdToken().getJwtToken());
        },
        onFailure: (err) => reject(err),
      }
    );
  });

/**
 * Initiate a forgot-password flow. Cognito sends a verification code to the
 * user's email. Call cognitoConfirmPassword() with that code to complete the reset.
 */
export const cognitoForgotPassword = (email: string): Promise<void> =>
  new Promise((resolve, reject) => {
    const user = new CognitoUser({ Username: email, Pool: userPool });
    user.forgotPassword({
      onSuccess: () => resolve(),
      onFailure: (err) => reject(err),
    });
  });

/**
 * Complete a forgot-password reset with the verification code and new password.
 */
export const cognitoConfirmPassword = (
  email: string,
  code: string,
  newPassword: string
): Promise<void> =>
  new Promise((resolve, reject) => {
    const user = new CognitoUser({ Username: email, Pool: userPool });
    user.confirmPassword(code, newPassword, {
      onSuccess: () => resolve(),
      onFailure: (err) => reject(err),
    });
  });

/** Sign out the current user. */
export const cognitoSignOut = (): void => {
  const user = userPool.getCurrentUser();
  if (user) user.signOut();
};

/**
 * Return a valid ID token JWT for the current session, refreshing silently if
 * needed. Returns null if no session exists (user not logged in).
 */
export const getCognitoToken = (): Promise<string | null> =>
  new Promise((resolve) => {
    const user = userPool.getCurrentUser();
    if (!user) {
      resolve(null);
      return;
    }
    user.getSession((err: Error | null, session: CognitoUserSession | null) => {
      if (err || !session || !session.isValid()) {
        resolve(null);
        return;
      }
      resolve(session.getIdToken().getJwtToken());
    });
  });
